<template>
  <div class="expense-payee-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        title="Expense by Payee"
        :start="start"
        :end="end"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">Expense by Payee</div>
              <div class="report-subtitle">
                Review spending totals and open a payee to see individual expenses.
              </div>
            </div>

            <v-spacer />

            <div class="summary-grid">
              <div class="summary-box">
                <div class="summary-label">Payees</div>
                <div class="summary-value">{{ payeeCount }}</div>
              </div>
              <div class="summary-box total-summary">
                <div class="summary-label">Total Expenses</div>
                <div class="summary-value">{{ total | currency }}</div>
              </div>
              <div class="summary-box">
                <div class="summary-label">Average per Payee</div>
                <div class="summary-value">{{ averageExpense | currency }}</div>
              </div>
              <div class="summary-box">
                <div class="summary-label">Largest Payee</div>
                <div class="summary-value summary-name" :title="largestPayee.name">
                  {{ largestPayee.name }}
                </div>
                <div v-if="largestPayee.amount" class="summary-detail">
                  {{ largestPayee.amount | currency }}
                </div>
              </div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="report-controls no-print">
            <v-text-field
              v-model="search"
              outlined
              rounded
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              label="Search payee"
              class="search-field"
            />

            <v-spacer />

            <v-btn
              text
              small
              color="primary"
              :disabled="!filteredExpenses.length"
              @click="toggleAll"
            >
              <v-icon left small>{{ allExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              {{ allExpanded ? "Collapse all" : "Expand all" }}
            </v-btn>
          </v-card-text>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear v-if="isLoading" indeterminate height="3" />

            <v-data-table
              v-else
              dense
              readonly
              item-key="id"
              sort-by="totalExpenses"
              :sort-desc="true"
              :mobile-breakpoint="0"
              :headers="headers"
              :items-per-page="-1"
              hide-default-footer
              :expanded.sync="expanded"
              show-expand
              :items="filteredExpenses"
              class="expense-payee-table"
            >
              <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
                <v-btn class="no-print" icon small @click="expand(!isExpanded)">
                  <v-icon color="primary">
                    {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>
                </v-btn>
              </template>

              <template v-slot:item.name="{ item }">
                <div class="payee-cell">
                  <v-avatar size="32" color="blue lighten-5" class="mr-3">
                    <v-icon small color="primary">mdi-account-cash-outline</v-icon>
                  </v-avatar>
                  <span>{{ item.name || "Unnamed payee" }}</span>
                </div>
              </template>

              <template v-slot:item.share="{ item }">
                <div class="share-cell">
                  <div class="share-track">
                    <div class="share-fill" :style="{ width: `${expenseShare(item)}%` }" />
                  </div>
                  <span>{{ expenseShare(item).toFixed(1) }}%</span>
                </div>
              </template>

              <template v-slot:item.totalExpenses="{ item }">
                <span class="expense-amount">{{ item.totalExpenses | currency }}</span>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <div class="expanded-panel">
                    <div class="expanded-heading">
                      <div>
                        <div class="expanded-title">{{ item.name || "Payee" }} expenses</div>
                        <div class="expanded-subtitle">Transactions included in this report period</div>
                      </div>
                      <v-chip small color="primary" outlined>
                        {{ item.totalExpenses | currency }}
                      </v-chip>
                    </div>
                    <ExpenseList :PayeeId="item.id" />
                  </div>
                </td>
              </template>

              <template v-slot:body.append v-if="filteredExpenses.length">
                <tr class="total-row">
                  <td></td>
                  <td class="text-right font-weight-bold">Total</td>
                  <td></td>
                  <td class="text-right font-weight-bold">{{ total | currency }}</td>
                </tr>
              </template>

              <template v-slot:no-data>
                <div class="empty-state">
                  <v-icon size="38" color="blue-grey lighten-2">mdi-account-search-outline</v-icon>
                  <div class="empty-title">No payee expenses found</div>
                  <div class="empty-subtitle">Try another search or report date range.</div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import ExpenseList from "@/modules/ReportView/components/subComponents/Expense";
import payeeService from "@/modules/Expense/Payee/service";

export default {
  name: "expense-by-payee-report",

  components: { ReportViewBar, ExpenseList },

  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      Expense: [],
      expanded: [],
      search: "",
      headers: [
        { text: "", value: "data-table-expand", sortable: false, width: 56 },
        { text: "Payee", align: "left", value: "name", sortable: true },
        { text: "Share of Total", align: "left", value: "share", sortable: false, width: 220 },
        { text: "Total Expenses", align: "right", value: "totalExpenses", sortable: true },
      ],
    };
  },

  computed: {
    filteredExpenses() {
      const term = (this.search || "").trim().toLowerCase();
      const rows = (this.Expense || []).filter(
        (row) => Number(row.totalExpenses || 0) > 0
      );

      if (!term) return rows;
      return rows.filter((row) =>
        String(row.name || "").toLowerCase().includes(term)
      );
    },

    total() {
      return this.filteredExpenses.reduce(
        (sum, row) => sum + Number(row.totalExpenses || 0),
        0
      );
    },

    payeeCount() {
      return this.filteredExpenses.length;
    },

    averageExpense() {
      return this.payeeCount ? this.total / this.payeeCount : 0;
    },

    largestPayee() {
      if (!this.filteredExpenses.length) return { name: "--", amount: 0 };

      const row = this.filteredExpenses.reduce((largest, current) =>
        Number(current.totalExpenses || 0) > Number(largest.totalExpenses || 0)
          ? current
          : largest
      );

      return {
        name: row.name || "Unnamed payee",
        amount: Number(row.totalExpenses || 0),
      };
    },

    allExpanded() {
      return (
        this.filteredExpenses.length > 0 &&
        this.expanded.length === this.filteredExpenses.length
      );
    },

    exportFields() {
      return {
        Payee: "name",
        "Share of Total": "share",
        "Total Expenses": "totalExpenses",
      };
    },

    exportData() {
      return this.filteredExpenses.map((row) => ({
        name: row.name || "Unnamed payee",
        share: `${this.expenseShare(row).toFixed(1)}%`,
        totalExpenses: Number(row.totalExpenses || 0),
      }));
    },
  },

  async created() {
    this.start = this.$route.query.start;
    this.end = this.$route.query.end;
    await this.getPayeeExpenses();
  },

  methods: {
    expenseShare(item) {
      if (!this.total) return 0;
      return (Number(item.totalExpenses || 0) / this.total) * 100;
    },

    toggleAll() {
      this.expanded = this.allExpanded ? [] : [...this.filteredExpenses];
    },

    async getPayeeExpenses() {
      this.isLoading = true;

      try {
        const response = await payeeService.getAllPayeeExpenseTotal({
          start: this.start,
          end: this.end,
        });

        this.Expense = response.data || [];
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.expense-payee-report { background: #f5f7fb; min-height: 100vh; padding: 16px; }
.report-page-card { border-radius: 18px; overflow: hidden; }
.report-content { padding: 20px; }
.report-table-card { border-radius: 16px; overflow: hidden; }
.report-table-header { gap: 18px; padding: 18px 20px; }
.report-title { color: #1f2937; font-size: 18px; font-weight: 800; }
.report-subtitle, .expanded-subtitle, .empty-subtitle { color: #6b7280; font-size: 12px; margin-top: 3px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(125px, 1fr)); gap: 8px; }
.summary-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; min-width: 125px; padding: 9px 12px; text-align: right; }
.summary-label { color: #6b7280; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.summary-value { color: #111827; font-size: 15px; font-weight: 800; margin-top: 2px; }
.summary-name { max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.summary-detail { color: #64748b; font-size: 11px; }
.total-summary { background: #eef4ff; border-color: #bfdbfe; }
.total-summary .summary-value { color: #1d4ed8; }
.report-controls { align-items: center; display: flex; gap: 12px; padding: 14px 20px; }
.search-field { max-width: 440px; }
.payee-cell { align-items: center; color: #334155; display: flex; font-weight: 700; text-transform: capitalize; }
.share-cell { align-items: center; color: #64748b; display: flex; font-size: 11px; gap: 9px; }
.share-track { background: #e2e8f0; border-radius: 999px; height: 6px; overflow: hidden; width: 110px; }
.share-fill { background: var(--v-primary-base); border-radius: inherit; height: 100%; }
.expense-amount { color: #0f172a; font-weight: 800; }
.expanded-panel { background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 14px 18px; }
.expanded-heading { align-items: center; display: flex; justify-content: space-between; margin-bottom: 4px; }
.expanded-title { color: #334155; font-size: 14px; font-weight: 800; text-transform: capitalize; }
.total-row { background: #f8fafc; }
.empty-state { color: #64748b; padding: 42px 16px; text-align: center; }
.empty-title { color: #334155; font-size: 16px; font-weight: 800; margin-top: 8px; }

@media print {
  .expense-payee-report, .report-content { padding: 0; }
  .report-page-card, .report-table-card { border: 0 !important; }
  .expanded-panel { break-inside: avoid; }
}

@media (max-width: 1000px) {
  .report-table-header { align-items: flex-start; flex-direction: column; }
  .summary-grid { grid-template-columns: repeat(2, minmax(145px, 1fr)); width: 100%; }
}

@media (max-width: 600px) {
  .expense-payee-report { padding: 0; }
  .report-content { padding: 10px; }
  .summary-grid { grid-template-columns: 1fr; }
  .report-controls { align-items: stretch; flex-direction: column; }
  .search-field { max-width: none; }
}
</style>
